import React, {
  useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import './TakeTest.css';
import TestWaitingPage from './TestWaitingPage';
import { usePopup } from '../context/PopupContext';
import TestResultScreen from '../components/practice/TestResultScreen';
import QuestionImage from '../components/ui/QuestionImage';
import QuestionText from '../components/ui/QuestionText';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const TAB_WARN_AT = 1; // show warning from 1st tab switch onward
const TAB_LIMIT   = 3; // auto-submit on 3rd tab switch
const FS_WARN_AT  = 1; // show warning from 1st fullscreen exit onward
const FS_LIMIT    = 2; // auto-submit on 2nd fullscreen exit

// ─────────────────────────────────────────────────────────────────────────────
// MEMOIZED NAVIGATOR  (never re-renders on timer tick)
// ─────────────────────────────────────────────────────────────────────────────
const QuestionNavigator = React.memo(({
  questions, current, getNavStatus, setCurrent, setShowExplanation,
}) => (
  <div className="tt-nav-grid">
    {questions.map((_, idx) => (
      <button
        key={idx}
        className={`tt-nav-btn ${getNavStatus(idx)} ${idx === current ? 'current' : ''}`}
        onClick={() => { setCurrent(idx); setShowExplanation(false); }}
      >
        {idx + 1}
      </button>
    ))}
  </div>
));

// ─────────────────────────────────────────────────────────────────────────────
// ANTI-CHEAT OVERLAY
// ─────────────────────────────────────────────────────────────────────────────
const Overlay = ({ kind, count, limit, testName, onDismiss, autoSubmitting }) => {
  const isTab    = kind === 'tab';
  const isFinal  = autoSubmitting;
  const remaining = limit - count;

  return (
    <div className="ac-overlay-bg">
      <div className={`ac-overlay-card ${isFinal ? 'ac-final' : 'ac-warn'}`}>
        <div className="ac-overlay-icon"><Icon name={isFinal ? 'error' : 'warning'} size={44} /></div>

        <h2 className="ac-overlay-title">
          {isFinal
            ? 'Test Auto-Submitted'
            : isTab
              ? `Tab Switch Detected — Warning ${count} of ${limit - 1}`
              : `Fullscreen Exit Detected — Warning ${count} of ${limit - 1}`}
        </h2>

        <p className="ac-overlay-body">
          {isFinal
            ? `"${testName}" was automatically submitted because you exceeded the allowed number of ${isTab ? 'tab switches' : 'fullscreen exits'}. All answers saved so far have been recorded.`
            : isTab
              ? remaining > 1
                ? `You left the test window. You have ${remaining - 1} warning${remaining - 1 > 1 ? 's' : ''} left before your test is auto-submitted.`
                : `LAST WARNING — the next tab switch will instantly auto-submit your test.`
              : remaining > 1
                ? `You exited fullscreen. You have ${remaining - 1} warning${remaining - 1 > 1 ? 's' : ''} left.`
                : `LAST WARNING — the next fullscreen exit will instantly auto-submit your test.`}
        </p>

        {!isFinal && (
          <>
            {!isTab && (
              <p className="ac-overlay-hint">
                Press <kbd>F11</kbd> or click the button below to re-enter fullscreen.
              </p>
            )}
            <button className="ac-overlay-btn" onClick={onDismiss}>
              {isTab ? 'I Understand — Return to Test' : 'Re-enter Fullscreen & Continue'}
            </button>
          </>
        )}

        {isFinal && (
          <p className="ac-overlay-hint" style={{ marginTop: 12 }}>
            Redirecting to your results…
          </p>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TakeTest = ({ isMockTest = false }) => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();

  // Determine mock mode once synchronously — stable for the lifetime of this mount
  const isMock = useMemo(
    () => isMockTest || window.location.pathname.startsWith('/student-mock'),
    [], // eslint-disable-line
  );

  // ── Test data ──────────────────────────────────────────────────────────────
  const [test, setTest]                           = useState(null);
  const [questions, setQuestions]                 = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [testStatus, setTestStatus]               = useState('loading');
  const [scheduledAt, setScheduledAt]             = useState(null);
  const [globalScheduledAt, setGlobalScheduledAt] = useState(null);

  // ── Exam state ─────────────────────────────────────────────────────────────
  const [current, setCurrent]           = useState(0);
  const [answers, setAnswers]           = useState({});
  const [marked, setMarked]             = useState(new Set());
  const [timeLeft, setTimeLeft]         = useState(0);
  const [submitted, setSubmitted]       = useState(false);
  const [result, setResult]             = useState(null);
  // See doSubmit's catch block — distinct from result.error (a
  // server-processed failure); this is for when the submit request never
  // reached the server at all, which is safely retryable.
  const [submitError, setSubmitError]   = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [playerDelta, setPlayerDelta]   = useState(null); // real before/after Level/XP diff only — see doSubmit

  // ── Anti-cheat state ───────────────────────────────────────────────────────
  const [tabCount, setTabCount]         = useState(0);
  const [fsCount, setFsCount]           = useState(0);
  const [overlay, setOverlay]           = useState(null); // null | 'tab' | 'fs'
  const [autoSubmitting, setAutoSubmitting] = useState(false);

  // ── Behavioral state ───────────────────────────────────────────────────────
  const eventQueue      = useRef([]);
  const qEnterTime      = useRef(Date.now());
  const lastInteract    = useRef(Date.now());
  const firstActionMap  = useRef(new Set());
  const visitedMap      = useRef(new Set());
  const timeWarnFired   = useRef(false);

  const dispatchEvent = useCallback((type, payload = {}) => {
    eventQueue.current.push({ type, timestamp: new Date().toISOString(), ...payload });
  }, []);

  // M10: send pending telemetry in chunks the server accepts (max 200 events per request). Used by the 10s timer AND
  // awaited on submit/unmount so the tail of the attempt (incl. the last question's dwell time) is not lost. A failed
  // chunk goes back to the front of the queue, so the next flush retries it.
  const flushEvents = useCallback(async () => {
    while (eventQueue.current.length > 0) {
      const chunk = eventQueue.current.splice(0, 200);
      try {
        await API.post(`/tests/${id}/events`, { events: chunk });
      } catch (e) {
        eventQueue.current.unshift(...chunk);
        return false;
      }
    }
    return true;
  }, [id]);

  // ── Refs (always up-to-date inside event listeners / timers) ──────────────
  const tabRef          = useRef(0);
  const fsRef           = useRef(0);
  const submittedRef    = useRef(false);
  const beforeProgressRef = useRef(null);
  const answersRef      = useRef({});
  const questionsRef    = useRef([]);
  const currentRef      = useRef(0);
  const startTime       = useRef(Date.now());
  const fsEnteredOnce   = useRef(false);  // ignore FS exit before user ever enters FS
  const autoSubmitGuard = useRef(false);  // prevent double auto-submit
  const timerFired      = useRef(false);  // prevent double timer auto-submit
  const fsLaunched      = useRef(false);  // enter FS only once

  // Keep answer/question/current refs in sync with state
  useEffect(() => { answersRef.current  = answers;   }, [answers]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);
  useEffect(() => { currentRef.current = current; }, [current]);

  // ─────────────────────────────────────────────────────────────────────────
  // FULLSCREEN HELPERS
  // ─────────────────────────────────────────────────────────────────────────
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    try {
      let result;
      if      (el.requestFullscreen)           result = el.requestFullscreen();
      else if (el.webkitRequestFullscreen)     result = el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen)        result = el.mozRequestFullScreen();
      // Browsers reject this promise when not called from a direct user gesture
      // (e.g. fired from a setTimeout); swallow it instead of an unhandled rejection.
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (_) {}
  }, []);

  const exitFullscreen = useCallback(() => {
    // Guard: only call if document is active AND something is actually fullscreen
    if (!document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.mozFullScreenElement) return;
    try {
      if      (document.exitFullscreen)           document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen)     document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen)      document.mozCancelFullScreen();
    } catch (_) {}
  }, []);

  const isInFullscreen = () =>
    !!(document.fullscreenElement ||
       document.webkitFullscreenElement ||
       document.mozFullScreenElement);

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT  — uses refs so it's never stale inside closures / timers
  // ─────────────────────────────────────────────────────────────────────────
  const doSubmit = useCallback(async ({ forceCheat = false, timeUp = false } = {}) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);

    if (!isMock) exitFullscreen();

    // M10: record how long the student spent on the question they are looking at (it is otherwise only recorded when they
    // navigate away, i.e. never for the last question) and flush every pending event BEFORE the attempt closes — the
    // server refuses telemetry for a submitted attempt.
    const cq = questionsRef.current[currentRef.current];
    if (cq) {
      dispatchEvent('question_left', { question_id: cq.id, time_spent_ms: Date.now() - qEnterTime.current });
      if (!answersRef.current[cq.id]) dispatchEvent('question_skipped', { question_id: cq.id });
    }
    try { await flushEvents(); } catch (_) { /* telemetry must never block a submission */ }

    const timeTaken  = Math.floor((Date.now() - startTime.current) / 1000);
    const answersArr = questionsRef.current.map(q => ({
      question_id:     q.id,
      selected_option: answersRef.current[q.id] ?? null,
    }));

    setSubmitError('');
    try {
      const res = await API.post(`/tests/${id}/submit`, {
        answers:            answersArr,
        time_taken_seconds: timeTaken,
        tab_switch_count:   tabRef.current,
        fs_exit_count:      fsRef.current,
        force_cheat:        forceCheat,
        time_up:            timeUp,
      });
      setResult({ ...res.data.result, forceCheat, timeUp: timeUp || !!res.data.expired });
      // Real before/after diff of the same computed Level/XP shown on Home —
      // never a fabricated event, silently skipped if either read fails.
      API.get('/portal/player-profile').then(after => {
        const before = beforeProgressRef.current;
        if (!before) return;
        setPlayerDelta({
          xpGained: Math.max(0, after.data.xp - before.xp),
          fromLevel: before.level,
          toLevel: after.data.level,
        });
      }).catch(() => {});
    } catch (err) {
      if (err.response?.data?.message === 'Test already submitted') {
        navigate('/student/tests');
      } else if (!err.response) {
        // No HTTP response reached us at all (offline/DNS/aborted) — the
        // submission never reached the server, so nothing was recorded.
        // Found in QA: the old fallback here claimed a result had been
        // "recorded" and showed a fabricated 0/0/0 breakdown even when the
        // request never left the browser. Reset the guard so the exam view
        // (with the student's answers still intact) is retryable instead —
        // the anti-cheat trigger that called doSubmit is untouched, only
        // the outcome-of-a-failed-network-call handling changes.
        submittedRef.current = false;
        setSubmitted(false);
        setSubmitError("Couldn't reach the server. Check your connection and try submitting again — your answers are still here.");
      } else {
        setResult({
          error: true, forceCheat, timeUp,
          score: 0, correct: 0, wrong: 0,
          total: questionsRef.current.length, percentage: '0',
        });
      }
    }
  }, [id, isMock, navigate, exitFullscreen, dispatchEvent, flushEvents]);

  // Autosave: persists each selection server-side so a refresh, crash or
  // dropped connection never loses answers. Fire-and-forget with one retry on
  // a network failure; the final submit still sends every answer, so a failed
  // autosave is never fatal. If the server reports the attempt is closed
  // (time is up), finish through the normal submit path to show the result.
  const autosave = useCallback(async (questionId, selectedOption) => {
    if (submittedRef.current) return;
    const send = () => API.post(`/tests/${id}/save-answer`, { question_id: questionId, selected_option: selectedOption });
    try {
      await send();
    } catch (err) {
      if (err.response?.data?.code === 'ATTEMPT_CLOSED') {
        if (!submittedRef.current) doSubmit({ timeUp: true });
      } else if (!err.response) {
        setTimeout(() => { send().catch(() => {}); }, 2000);
      }
    }
  }, [id, doSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // TRIGGER AUTO-SUBMIT  (guarded — fires exactly once)
  // ─────────────────────────────────────────────────────────────────────────
  const triggerAutoSubmit = useCallback((reason) => {
    if (autoSubmitGuard.current || submittedRef.current) return;
    autoSubmitGuard.current = true;
    setAutoSubmitting(true);
    setOverlay(reason);          // overlay stays open showing "auto-submitted"
    setTimeout(() => doSubmit({ forceCheat: true }), 3000);
  }, [doSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // ANTI-CHEAT EVENT LISTENERS  (real tests only, registered once)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMock) return; // ← ZERO anti-cheat for mock tests

    // ── 1. Tab / visibility detection ─────────────────────────────────────
    let hiddenTimer = null;
    const onVisibility = () => {
      if (document.hidden) {
        dispatchEvent('tab_blur');
        // Debounce 600 ms to filter accidental OS focus loss
        hiddenTimer = setTimeout(() => {
          if (submittedRef.current) return;
          const next = tabRef.current + 1;
          tabRef.current = next;
          setTabCount(next);
          if (next >= TAB_LIMIT) {
            triggerAutoSubmit('tab');
          } else if (next >= TAB_WARN_AT) {
            setOverlay('tab');
          }
        }, 600);
      } else {
        dispatchEvent('tab_focus');
        // Came back within debounce window — cancel
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
    };

    // ── 2. Fullscreen exit detection ──────────────────────────────────────
    const onFsChange = () => {
      if (submittedRef.current) return;
      if (isInFullscreen()) {
        // Student entered fullscreen — start tracking exits from now
        fsEnteredOnce.current = true;
        return;
      }
      // Don't penalise before they've ever entered fullscreen
      if (!fsEnteredOnce.current) return;

      const next = fsRef.current + 1;
      fsRef.current = next;
      setFsCount(next);
      if (next >= FS_LIMIT) {
        triggerAutoSubmit('fs');
      } else if (next >= FS_WARN_AT) {
        setOverlay('fs');
      }
    };

    // ── 3. Block right-click ──────────────────────────────────────────────
    const onContextMenu = (e) => e.preventDefault();

    // ── 4. Block copy / cut / paste ───────────────────────────────────────
    const onCopy  = (e) => e.preventDefault();
    const onCut   = (e) => e.preventDefault();
    const onPaste = (e) => e.preventDefault();

    // ── 5. Block dangerous keyboard shortcuts ────────────────────────────
    const onKeyDown = (e) => {
      if (submittedRef.current) return;
      const k = e.key.toLowerCase();
      if (e.key === 'F12')                                              { e.preventDefault(); return; }
      if (e.key === 'PrintScreen')                                      { e.preventDefault(); return; }
      if (e.ctrlKey && e.shiftKey && ['i','j','c','u'].includes(k))    { e.preventDefault(); return; }
      if (e.ctrlKey && k === 'u')                                       { e.preventDefault(); return; }
    };

    // ── 6. Warn before tab close / navigate away ─────────────────────────
    const onBeforeUnload = (e) => {
      if (submittedRef.current) return;
      e.preventDefault();
      e.returnValue = 'Your test is in progress. Leaving will count as a violation.';
    };

    // ── 7. Idle detection ────────────────────────────────────────────────
    const idleChecker = setInterval(() => {
      if (submittedRef.current) return;
      if (Date.now() - lastInteract.current > 30000) {
        const activeQ = questionsRef.current[currentRef.current];
        dispatchEvent('idle_detected', { 
          question_id: activeQ?.id,
          metadata: { idle_duration_ms: Date.now() - lastInteract.current } 
        });
        lastInteract.current = Date.now(); // reset to prevent spam
      }
    }, 5000);

    const onInteract = () => { lastInteract.current = Date.now(); };
    window.addEventListener('mousemove', onInteract);
    window.addEventListener('keydown', onInteract);
    window.addEventListener('click', onInteract);

    // ── 8. Batch Event Flusher ───────────────────────────────────────────
    const flushInterval = setInterval(() => {
      if (eventQueue.current.length === 0 || submittedRef.current) return;
      flushEvents();
    }, 10000);

    document.addEventListener('visibilitychange',       onVisibility);
    document.addEventListener('fullscreenchange',       onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange',    onFsChange);
    document.addEventListener('contextmenu',            onContextMenu);
    document.addEventListener('copy',                   onCopy);
    document.addEventListener('cut',                    onCut);
    document.addEventListener('paste',                  onPaste);
    document.addEventListener('keydown',                onKeyDown);
    window.addEventListener('beforeunload',             onBeforeUnload);

    return () => {
      clearTimeout(hiddenTimer);
      clearInterval(idleChecker);
      clearInterval(flushInterval);
      window.removeEventListener('mousemove', onInteract);
      window.removeEventListener('keydown', onInteract);
      window.removeEventListener('click', onInteract);
      document.removeEventListener('visibilitychange',       onVisibility);
      document.removeEventListener('fullscreenchange',       onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange',    onFsChange);
      document.removeEventListener('contextmenu',            onContextMenu);
      document.removeEventListener('copy',                   onCopy);
      document.removeEventListener('cut',                    onCut);
      document.removeEventListener('paste',                  onPaste);
      document.removeEventListener('keydown',                onKeyDown);
      window.removeEventListener('beforeunload',             onBeforeUnload);
    };
  }, [isMock, triggerAutoSubmit, flushEvents]); // stable — runs exactly once

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH TEST
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await API.get(`/tests/${id}/student`);
        const { test: t, questions: qs, scheduledAt: perSAt, remainingSeconds, savedAnswers } = res.data;

        setTest(t);
        setQuestions(qs);
        questionsRef.current = qs;
        // The countdown is anchored to the SERVER's clock: `remainingSeconds`
        // is derived from the attempt's server-recorded start time, so neither
        // a refresh nor a tampered device clock can change the deadline. (The
        // server also rejects late saves/submits regardless of this display.)
        const durationSeconds = (t.duration_minutes || 30) * 60;
        setTimeLeft(Number.isFinite(remainingSeconds) ? remainingSeconds : durationSeconds);
        // Restore answers autosaved before a refresh / disconnect.
        if (savedAnswers && typeof savedAnswers === 'object') {
          setAnswers(savedAnswers);
          answersRef.current = savedAnswers;
        }

        // Best-effort "before" snapshot of real Level/XP for the honest
        // post-submit delta in doSubmit below — never blocks the exam view.
        API.get('/portal/player-profile').then(res => { beforeProgressRef.current = res.data; }).catch(() => {});

        const globalSAt = t.scheduled_at || null;
        setScheduledAt(perSAt || null);
        setGlobalScheduledAt(globalSAt);
        resolveStatus(globalSAt, perSAt || null);

      } catch (err) {
        const data    = err.response?.data || {};
        const msg     = data.message;
        const attempt = data.attempt;

        if (msg === 'already_submitted' && attempt) {
          // `data.result` is the server's own result payload (marks, negative
          // marking and unattempted are already resolved) — not recomputed here.
          const r = data.result || {};
          const total = r.total ?? attempt.total_questions ?? 0;
          setTest({ test_name: 'Test', total_marks: total });
          submittedRef.current = true;
          setSubmitted(true);
          setResult({
            score:             r.score ?? (Number(attempt.score) || 0),
            correct:           r.correct ?? (attempt.correct_answers || 0),
            wrong:             r.wrong ?? (attempt.wrong_answers || 0),
            unattempted:       r.unattempted ?? (attempt.unattempted_answers || 0),
            total,
            percentage:        r.percentage ?? '0',
            cheating_flag:     attempt.cheating_flag || false,
            already_submitted: true,
            timeUp:            !!data.expired,
          });
          setTestStatus('ready');
        } else if (data.code === 'NOT_OPEN_YET' && data.opens_at) {
          // The server does not hand out questions before the scheduled time:
          // show the waiting screen; it reloads the page when the time arrives.
          setTest({ test_name: data.test_name || 'Assessment', duration_minutes: data.duration_minutes, total_marks: data.total_marks });
          // Align the countdown to the SERVER's clock (not this device's), so a
          // fast/slow device clock can't reload too early and bounce off the
          // server's NOT_OPEN_YET again.
          // (The waiting page itself adds the lobby minutes to the scheduled time.)
          const offset = data.server_now ? new Date(data.server_now).getTime() - Date.now() : 0;
          setScheduledAt(new Date(new Date(data.scheduled_at || data.opens_at).getTime() - offset + 1000).toISOString());
          setTestStatus('waiting');
        } else if (data.code === 'WINDOW_CLOSED') {
          setTest({ test_name: data.test_name || 'Assessment' });
          setTestStatus('closed');
        } else {
          showAlert('Error', err.response?.data?.message || 'Failed to load test.');
          navigate('/student/tests');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [id, navigate]); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────────
  // TIMING  (waiting / ready / closed)
  // ─────────────────────────────────────────────────────────────────────────
  const resolveStatus = useCallback((globalSAt, perSAt) => {
    const effective = perSAt || globalSAt;
    if (!effective) { setTestStatus('ready'); return; }
    const now    = Date.now();
    const start  = new Date(effective).getTime();
    const grace  = start + 5  * 60_000;
    const closes = start + 10 * 60_000;
    if      (now < grace)  setTestStatus('waiting');
    else if (now < closes) setTestStatus('ready');
    else                   setTestStatus('closed');
  }, []);

  // Poll while waiting for the scheduled time
  useEffect(() => {
    if (submitted || testStatus === 'ready' || testStatus === 'closed') return;
    if (!scheduledAt && !globalScheduledAt) return;
    if (questions.length === 0) return; // waiting on the server's opening time, not the local clock
    const iv = setInterval(() => resolveStatus(globalScheduledAt, scheduledAt), 1000);
    return () => clearInterval(iv);
  }, [scheduledAt, globalScheduledAt, submitted, testStatus, resolveStatus, questions.length]);

  // TestWaitingPage calls this when its countdown hits zero
  const handleTimeReached = useCallback(() => {
    // If the server withheld the questions (opened too early), fetch them now.
    if (questionsRef.current.length === 0) window.location.reload();
    else setTestStatus('ready');
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // ENTER FULLSCREEN once test becomes ready  (real tests only)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMock)                  return;
    if (fsLaunched.current)      return;
    if (testStatus !== 'ready')  return;
    if (submitted)               return;
    if (questions.length === 0)  return;
    fsLaunched.current = true;
    setTimeout(enterFullscreen, 400); // short delay so UI has settled
  }, [testStatus, submitted, questions.length, isMock, enterFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────
  // TIMER COUNTDOWN
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (testStatus !== 'ready')              return;
    if (submitted || questions.length === 0) return;

    const iv = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          // Fire auto-submit outside the setState callback
          if (!timerFired.current && !submittedRef.current) {
            timerFired.current = true;
            setTimeout(() => doSubmit({ timeUp: true }), 0);
          }
          return 0;
        }
        if (prev <= 300 && prev > 1 && !timeWarnFired.current) {
          timeWarnFired.current = true;
          dispatchEvent('time_warning_reaction', { metadata: { time_left_seconds: prev } });
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(iv);
  }, [testStatus, submitted, questions.length, doSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // QUESTION VISIT TRACKING
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!questions || questions.length === 0 || submitted || testStatus !== 'ready') return;
    const q = questions[current];
    if (!q) return;

    if (visitedMap.current.has(q.id)) {
      dispatchEvent('question_revisited', { question_id: q.id });
    } else {
      dispatchEvent('question_opened', { question_id: q.id });
      visitedMap.current.add(q.id);
    }
    qEnterTime.current = Date.now();

    return () => {
      // Cleanup: user is leaving the question
      const timeSpent = Date.now() - qEnterTime.current;
      dispatchEvent('question_left', { 
        question_id: q.id, 
        time_spent_ms: timeSpent 
      });

      if (!answersRef.current[q.id]) {
        dispatchEvent('question_skipped', { question_id: q.id });
      }
    };
  }, [current, questions, submitted, testStatus, dispatchEvent]);

  // ─────────────────────────────────────────────────────────────────────────
  // OVERLAY DISMISS HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const dismissTabOverlay = useCallback(() => {
    if (autoSubmitting) return;
    setOverlay(null);
  }, [autoSubmitting]);

  const dismissFsOverlay = useCallback(() => {
    if (autoSubmitting) return;
    setOverlay(null);
    enterFullscreen();
  }, [autoSubmitting, enterFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────
  // QUESTION NAVIGATOR STATUS  (memoized — stable, no re-renders from timer)
  // ─────────────────────────────────────────────────────────────────────────
  const getNavStatus = useCallback((idx) => {
    const q = questions[idx];
    if (!q) return 'unvisited';
    if (marked.has(idx))             return 'marked';
    if (answers[q.id] !== undefined) return 'answered';
    return 'unvisited';
  }, [questions, marked, answers]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER GUARDS
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="tt-loading">
      <div style={{ marginBottom: 8 }}><Icon name="clock" size={32} /></div>
      Loading test…
    </div>
  );

  if (testStatus === 'waiting' && test && !result) {
    return (
      <TestWaitingPage
        testName={test.test_name}
        testDuration={test.duration_minutes}
        testMarks={test.total_marks}
        scheduledTime={scheduledAt || globalScheduledAt}
        onTimeReached={handleTimeReached}
      />
    );
  }

  if (testStatus === 'closed' && !submitted && !result && test) {
    return (
      <div className="tt-closed-screen">
        <div className="tt-closed-card">
          <div className="tt-closed-icon"><Icon name="error" size={48} /></div>
          <h1 className="tt-closed-title">Test Window Closed</h1>
          <p className="tt-closed-message">The allowed time window for this test has expired.</p>
          <p className="tt-closed-submsg">Contact your administrator if you believe this is an error.</p>
          <button className="tt-close-btn" onClick={() => navigate('/student/tests')}>
            <Icon name="chevron-left" size={16} /> Back to Tests
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    // Normalize percentage the same way the old inline view did (some
    // fallback paths don't set it) — no scoring logic, just a display fallback.
    const normalizedResult = {
      ...result,
      percentage: result.percentage ?? (result.total > 0 ? ((result.score / result.total) * 100).toFixed(1) : '0'),
    };
    return (
      <TestResultScreen
        result={normalizedResult}
        variant="official"
        onPrimaryAction={() => navigate('/student/analytics', { state: { justCompleted: true } })}
        onSecondaryAction={() => navigate('/student/dashboard')}
        secondaryLabel="Back to Home"
        onReviewAction={() => navigate(`/student/test/${id}/review`)}
        playerDelta={playerDelta}
      />
    );
  }

  if (!test || questions.length === 0) return (
    <div className="tt-loading">
      No questions found for this test. Please contact admin.
      <button type="button" className="z-btn z-btn--primary" onClick={() => navigate('/student/tests')}>
        Go Back
      </button>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TEST UI
  // ─────────────────────────────────────────────────────────────────────────
  const q        = questions[current];
  const answered = Object.keys(answers).length;

  return (
    <div
      className="tt-wrapper"
      /* Disable text selection for real proctored tests */
      style={!isMock ? { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' } : {}}
    >

      {/* ── Anti-cheat overlay (real tests only) ──────────────────────────── */}
      {!isMock && overlay === 'tab' && (
        <Overlay
          kind="tab"
          count={tabCount}
          limit={TAB_LIMIT}
          testName={test.test_name}
          onDismiss={dismissTabOverlay}
          autoSubmitting={autoSubmitting}
        />
      )}
      {!isMock && overlay === 'fs' && (
        <Overlay
          kind="fs"
          count={fsCount}
          limit={FS_LIMIT}
          testName={test.test_name}
          onDismiss={dismissFsOverlay}
          autoSubmitting={autoSubmitting}
        />
      )}

      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <div className="tt-topbar">
        <div className="tt-topbar-left">
          <div className="tt-test-name">{test.test_name}</div>
          <div className="tt-session">
            {questions.length} questions
            {!isMock && (
              <span className="tt-proctored-badge"><Icon name="lock" size={12} /> Proctored</span>
            )}
          </div>
        </div>

        <div className="tt-topbar-center">
          <div className={`tt-timer ${timeLeft < 300 ? 'danger' : ''}`}>
            <Icon name="timer" size={18} /> {formatTime(timeLeft)}
          </div>
        </div>

        <div className="tt-topbar-right">
          {/* Live violation badges — appear after first violation */}
          {!isMock && tabCount > 0 && (
            <span className={`tt-violation-badge ${tabCount >= TAB_LIMIT - 1 ? 'danger' : 'warn'}`}>
              <Icon name="warning" size={14} /> Tabs {tabCount}/{TAB_LIMIT - 1}
            </span>
          )}
          {!isMock && fsCount > 0 && (
            <span className={`tt-violation-badge ${fsCount >= FS_LIMIT - 1 ? 'danger' : 'warn'}`}>
              <Icon name="warning" size={14} /> FS {fsCount}/{FS_LIMIT - 1}
            </span>
          )}
        </div>
        {/* Progress runs along the bottom edge of the bar, so it stays attached however tall the bar wraps. */}
        <div className="tt-progress-bar">
          <div className="tt-progress-fill"
            style={{ width: `${(answered / questions.length) * 100}%` }} />
        </div>
      </div>

      {submitError && <div className="tt-submit-error-banner">{submitError}</div>}

      <div className="tt-body">

        {/* ── Question area ─────────────────────────────────────────────────── */}
        <div className="tt-question-area">
          <div className="tt-q-header">
            <span className="tt-q-num">QUESTION {current + 1} OF {questions.length}</span>
            <span className="tt-q-diff">
              Difficulty: {q.difficulty?.charAt(0).toUpperCase() + q.difficulty?.slice(1)}
            </span>
          </div>

          <div className="tt-q-text"><QuestionText text={q.question_text} /></div>
          <QuestionImage path={q.image_path} />

          <div className="tt-options">
            {(q.options || []).map((opt, i) => {
              const optNum   = i + 1;
              const selected = answers[q.id] === optNum;
              return (
                <div
                  key={i}
                  className={`tt-option${selected ? ' selected' : ''}`}
                  onClick={() => {
                    if (!submitted) {
                      const prevAns = answers[q.id];
                      if (!prevAns) {
                        if (!firstActionMap.current.has(q.id)) {
                          firstActionMap.current.add(q.id);
                          dispatchEvent('first_interaction', { 
                            question_id: q.id, 
                            metadata: { time_to_first_action_ms: Date.now() - qEnterTime.current, action_type: 'option_select' } 
                          });
                        }
                        dispatchEvent('option_selected', { 
                          question_id: q.id, 
                          from_option: null, 
                          to_option: optNum,
                          time_to_first_answer_ms: Date.now() - qEnterTime.current
                        });
                      } else if (prevAns !== optNum) {
                        dispatchEvent('option_changed', { question_id: q.id, from_option: prevAns, to_option: optNum });
                      }
                      setAnswers(p => ({ ...p, [q.id]: optNum }));
                      autosave(q.id, optNum);
                      setShowExplanation(false);
                    }
                  }}
                >
                  <span className="tt-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="tt-opt-text">{opt}</span>
                </div>
              );
            })}
          </div>

          {q.explanation && (
            <button type="button" className="tt-explanation-toggle" aria-expanded={showExplanation}
              onClick={() => setShowExplanation(p => !p)}>
              <span><Icon name="info" size={16} /> View Step-by-Step Explanation</span>
              <Icon name={showExplanation ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
          )}
          {showExplanation && q.explanation && (
            <div className="tt-explanation">{q.explanation}</div>
          )}

          <div className="tt-bottom-nav">
            <button
              className="tt-btn-prev"
              disabled={current === 0}
              onClick={() => { setCurrent(p => p - 1); setShowExplanation(false); }}
            >
              <Icon name="chevron-left" size={16} /> Previous
            </button>
            <button
              className="tt-btn-mark"
              onClick={() => setMarked(p => {
                const n = new Set(p);
                n.has(current) ? n.delete(current) : n.add(current);
                return n;
              })}
            >
              <Icon name="flag" size={16} /> {marked.has(current) ? 'Marked' : 'Mark for Review'}
            </button>
            {current < questions.length - 1 ? (
              <button className="tt-btn-next"
                onClick={() => { setCurrent(p => p + 1); setShowExplanation(false); }}>
                Save & Next <Icon name="chevron-right" size={16} />
              </button>
            ) : (
              <button className="tt-btn-submit"
                onClick={async () => {
                  const confirmed = await showConfirm(
                    'Finish Test',
                    `Submit test?\nAnswered: ${answered} / ${questions.length}\n${questions.length - answered} question(s) unanswered.`
                  );
                  if (confirmed) doSubmit();
                }}>
                Finish & Submit
              </button>
            )}
          </div>
        </div>

        {/* ── Right sidebar ──────────────────────────────────────────────────── */}
        <div className="tt-sidebar">
          <div className="tt-nav-header">
            Question Navigator
            <span className="tt-nav-answered">{answered}/{questions.length}</span>
          </div>

          <QuestionNavigator
            questions={questions}
            current={current}
            getNavStatus={getNavStatus}
            setCurrent={setCurrent}
            setShowExplanation={setShowExplanation}
          />

          <div className="tt-nav-legend">
            <div className="tt-leg"><span className="tt-leg-dot answered" />Answered</div>
            <div className="tt-leg"><span className="tt-leg-dot marked"   />Marked</div>
            <div className="tt-leg"><span className="tt-leg-dot unvisited"/>Unvisited</div>
          </div>

          <button className="tt-finish-btn"
            onClick={async () => {
              const confirmed = await showConfirm('Finish Session', `Submit now? Answered ${answered}/${questions.length}.`);
              if (confirmed) doSubmit();
            }}>
            Finish Session
          </button>

          {isMock ? (
            <button className="tt-pause-btn" onClick={() => navigate('/student/mock')}>
              Exit Mock Test
            </button>
          ) : (
            <div className="tt-proctor-box">
              <div className="tt-proctor-title"><Icon name="lock" size={14} /> Proctored Test</div>
              <div className="tt-proctor-rule">· Stay on this tab at all times</div>
              <div className="tt-proctor-rule">· Keep fullscreen mode active</div>
              <div className="tt-proctor-rule">· No copying or right-clicking</div>
              <div className="tt-proctor-rule">· {TAB_LIMIT} tab violations = auto-submit</div>
              <div className="tt-proctor-rule">· {FS_LIMIT} fullscreen exits = auto-submit</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeTest;
